import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MatcherWidgetJSON, MatcherRowItem, MatcherItemContent, LayoutItem, WidgetJSON } from '../../../transformers/zod-schemas';
import type { ValidationStatus } from '../types';
import { LayoutRenderer } from '../../components/LayoutRenderer';
import { resolveGroupPalette, getTextColorForBackground } from '../../utils/group-palette';
import { useSound } from '../../sounds';

export interface MatcherWidgetProps {
  widgetId: string;
  widget: MatcherWidgetJSON;
  onChange: (widgetId: string, value: string[][]) => void;
  validationStatus?: ValidationStatus | null;
  /** Pre-populate state (e.g. restore saved progress). Same shape as onChange value. */
  defaultValue?: string[][];
  /** Widgets map for resolving widget-refs nested inside item content (e.g. display fractions). */
  widgets?: Record<string, WidgetJSON>;
}

/** colorIdx per itemId; null = unassigned. */
type Assignments = Record<string, number | null>;

function collectItemIds(rows: readonly MatcherRowItem[]): string[] {
  return rows.flatMap(row => row.items.map(item => item.itemId));
}

function initialAssignments(itemIds: string[]): Assignments {
  return Object.fromEntries(itemIds.map(id => [id, null]));
}

function assignmentsFromValue(value: string[][], itemIds: string[], groupCount: number): Assignments {
  const base = initialAssignments(itemIds);
  value.forEach((group, groupIdx) => {
    if (groupIdx >= groupCount) return;
    group.forEach(id => {
      if (id in base) base[id] = groupIdx;
    });
  });
  return base;
}

// Map preserves insertion (click) order, so group indices in the emitted
// value reflect the order the user selected them.
function buildGroups(assignments: Assignments): string[][] {
  const map = new Map<number, string[]>();
  for (const [itemId, colorIdx] of Object.entries(assignments)) {
    if (colorIdx === null) continue;
    if (!map.has(colorIdx)) map.set(colorIdx, []);
    map.get(colorIdx)!.push(itemId);
  }
  return Array.from(map.values());
}

// Stable refs — defined outside component to preserve referential equality.
const EMPTY_WIDGETS: Record<string, WidgetJSON> = {};
const EMPTY_VALUES: Record<string, never> = {};
const EMPTY_DROPPED = new Set<string>();
const NOOP = () => {};

// Reset is handled by the parent remounting via `key={resetKey}` — the
// state initializer runs again on each mount, picking up defaultValue afresh.
export const MatcherWidget: React.FC<MatcherWidgetProps> = ({
  widgetId,
  widget,
  onChange,
  validationStatus = null,
  defaultValue,
  widgets,
}) => {
  const { rows, colors: userColors, groupCount } = widget;
  const widgetsMap = widgets ?? EMPTY_WIDGETS;

  const palette = useMemo(
    () => resolveGroupPalette(userColors, groupCount),
    [userColors, groupCount],
  );

  const textColors = useMemo(
    () => palette.map(getTextColorForBackground),
    [palette],
  );

  const itemIds = useMemo(() => collectItemIds(rows), [rows]);

  const [assignments, setAssignments] = useState<Assignments>(
    () => defaultValue && defaultValue.length > 0
      ? assignmentsFromValue(defaultValue, itemIds, groupCount)
      : initialAssignments(itemIds),
  );

  const playSound = useSound();
  const handleCardClick = useCallback(
    (itemId: string) => {
      playSound('matcher-select');
      setAssignments(prev => {
        const current = prev[itemId] ?? null;
        const next = current === null
          ? 0
          : current + 1 >= groupCount
            ? null
            : current + 1;
        return { ...prev, [itemId]: next };
      });
    },
    [groupCount, playSound],
  );
  // Notify parent after state has settled — avoids setState-during-render warning.
  // Skip the initial mount to avoid spurious onChange on first render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onChangeRef.current(widgetId, buildGroups(assignments));
  }, [assignments, widgetId]);

  const maxCols = useMemo(
    () => (rows.length === 0 ? 1 : Math.max(...rows.map(r => r.items.length))),
    [rows],
  );

  return (
    <div
      className={`matcher-widget${validationStatus ? ` widget--${validationStatus}` : ''}`}
      style={{ '--matcher-cols': maxCols } as React.CSSProperties}
      role="group"
      aria-label="Matcher exercise"
    >
      {rows.map((row: MatcherRowItem) => (
        <div key={row.id} className="matcher-row">
          {row.items.map((item: MatcherItemContent) => {
            const colorIdx = assignments[item.itemId] ?? null;
            const isSelected = colorIdx !== null;
            const color = isSelected && !validationStatus ? palette[colorIdx] : undefined;

            return (
              <button
                key={item.id}
                type="button"
                className={`matcher-card${isSelected ? ' matcher-card--selected' : ''}`}
                style={
                  colorIdx !== null && color
                    ? {
                        '--matcher-card-color': `var(--matcher-group-${colorIdx + 1}-color, ${color})`,
                        '--matcher-card-text-color': `var(--matcher-group-${colorIdx + 1}-text-color, ${textColors[colorIdx]})`,
                      } as React.CSSProperties
                    : undefined
                }
                onClick={() => handleCardClick(item.itemId)}
                aria-pressed={isSelected}
                aria-label={isSelected ? `Assigned to group ${colorIdx + 1}` : 'Not assigned'}
              >
                {item.content.map((layoutItem: LayoutItem) => (
                  <LayoutRenderer
                    key={layoutItem.id}
                    item={layoutItem}
                    widgets={widgetsMap}
                    values={EMPTY_VALUES}
                    droppedInputs={EMPTY_DROPPED}
                    onChange={NOOP}
                  />
                ))}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
MatcherWidget.displayName = 'MatcherWidget';
